import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { AccountingPeriodService } from '../../../accounting/service/data/accounting-period.service';
import { LocalDate } from '../../../core/model/local-date';
import { ActionWindowService } from '../../../core/service/action-window.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { AccountingPeriodCloseResult, AccountingPeriodReopenInfo } from '../../../accounting/model/opening-balance-run';
import { Company } from '../../model/company';
import { SelectionItem } from '../../../core/component/select/select.component';

@Component({
  templateUrl: './set-lock-date.component.html',
  styleUrls: ['./set-lock-date.component.scss'],
  providers: [AccountingPeriodService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SetLockDateComponent implements OnInit {
  constructor(
    private _dialogService: ActionWindowService,
    private _fb: FormBuilder,
    private _formControlFactory: FormControlExFactory,
    private _service: AccountingPeriodService
  ) {}

  @Input() dataContext: { company?: Company; companies?: Company[]; mode: 'close' | 'reopen' };

  form: FormGroup;
  periodDate: LocalDate;
  reopenInfo: AccountingPeriodReopenInfo;
  months: SelectionItem<number>[] = [
    new SelectionItem('MONTH.JANUARY', 1),
    new SelectionItem('MONTH.FEBRUARY', 2),
    new SelectionItem('MONTH.MARCH', 3),
    new SelectionItem('MONTH.APRIL', 4),
    new SelectionItem('MONTH.MAY', 5),
    new SelectionItem('MONTH.JUNE', 6),
    new SelectionItem('MONTH.JULY', 7),
    new SelectionItem('MONTH.AUGUST', 8),
    new SelectionItem('MONTH.SEPTEMBER', 9),
    new SelectionItem('MONTH.OCTOBER', 10),
    new SelectionItem('MONTH.NOVEMBER', 11),
    new SelectionItem('MONTH.DECEMBER', 12),
  ];

  get isReopen(): boolean {
    return this.dataContext?.mode === 'reopen';
  }

  get titleKey(): string {
    return this.isReopen ? 'ACCOUNTING_PERIOD.REOPEN_HEADER' : 'ACCOUNTING_PERIOD.CLOSE_HEADER';
  }

  get descriptionKey(): string {
    return this.isReopen ? 'ACCOUNTING_PERIOD.REOPEN_DESCRIPTION' : 'ACCOUNTING_PERIOD.CLOSE_DESCRIPTION';
  }

  get companies(): Company[] {
    return this.dataContext?.companies?.length ? this.dataContext.companies : this.dataContext?.company ? [this.dataContext.company] : [];
  }

  get isMany(): boolean {
    return this.companies.length > 1;
  }

  get alreadyClosedCompanies(): Company[] {
    if (this.isReopen || !this.form) return [];

    const closeDate = this.getCloseDate();
    return this.companies.filter(company => this.isAlreadyClosed(company, closeDate));
  }

  get companiesToClose(): Company[] {
    if (this.isReopen || !this.form) return this.companies;

    const closeDate = this.getCloseDate();
    return this.companies.filter(company => !this.isAlreadyClosed(company, closeDate));
  }

  cancel() {
    this._dialogService.close();
  }

  ngOnInit() {
    this.periodDate =
      this.isReopen && this.dataContext.company?.accountingClosedTo?.hasValue()
        ? this.dataContext.company.accountingClosedTo.clone()
        : LocalDate.today();

    const formConfig: any = {};

    if (this.isReopen) {
      formConfig.periodDate = this._formControlFactory.createReadonly('ACCOUNTING_PERIOD.CLOSED_TO_DATE', this.periodDate);
      formConfig.reason = this._formControlFactory.createMaxLength('ACCOUNTING_PERIOD.REASON', '', 255);
      formConfig.confirmed = this._formControlFactory.createMandatory('ACCOUNTING_PERIOD.CONFIRMED', false);

      if (this.dataContext.company?.id) {
        this._service.getReopenInfo(this.dataContext.company.id).subscribe(info => {
          this.reopenInfo = info;
        });
      }
    } else {
      const today = LocalDate.today();
      formConfig.year = this._formControlFactory.createWithOptions('COMMON.YEAR', today.year, { required: true, minValue: 1900, maxValue: 2200 });
      formConfig.month = this._formControlFactory.createMandatory('COMMON.MONTH', today.month);
      formConfig.description = this._formControlFactory.createMaxLength('ACCOUNTING_PERIOD.DESCRIPTION', '', 255);
    }

    this.form = this._fb.group(formConfig);
  }

  private _working = false;
  onSubmit() {
    if (this.form.invalid || this._working) return;

    this._working = true;
    if (this.isReopen) {
      this._service
        .reopen(
          this.dataContext.company.id,
          this.form.controls['periodDate'].value,
          this.form.controls['reason'].value,
          this.form.controls['confirmed'].value
        )
        .subscribe(
          res => {
            if (res) {
              this._dialogService.complete(res);
            } else {
              this._working = false;
            }
          },
          () => {
            this._working = false;
          }
        );
      return;
    }

    if (this.isMany) {
          this._service
        .closeMany(
          this.companies.map(company => company.id),
          this.getCloseDate(),
          this.form.controls['description'].value
        )
        .subscribe(
          res => {
            if (res) {
              this.applyBatchResult(res);
              this._dialogService.complete(res);
            } else {
              this._working = false;
            }
          },
          () => {
            this._working = false;
          }
        );
      return;
    }

    this._service.close(this.companies[0].id, this.getCloseDate(), this.form.controls['description'].value).subscribe(
      res => {
        if (res) {
          this._dialogService.complete(res);
        } else {
          this._working = false;
        }
      },
      () => {
        this._working = false;
      }
    );
  }

  canSubmit(): boolean {
    if (this.form.invalid || this._working) return false;
    if (this.isReopen) return this.form.controls['confirmed'].value;
    return this.companiesToClose.length > 0;
  }

  private isAlreadyClosed(company: Company, closeDate: LocalDate): boolean {
    const accountingClosedTo = company?.accountingClosedTo;
    return (
      accountingClosedTo?.hasValue() &&
      (LocalDate.Equal(accountingClosedTo, closeDate) || LocalDate.GreaterThan(accountingClosedTo, closeDate))
    );
  }

  private getCloseDate(): LocalDate {
    const year = Number(this.form.controls['year'].value);
    const month = Number(this.form.controls['month'].value);
    return new LocalDate(year, month, LocalDate.daysInMonth(year, month));
  }

  private applyBatchResult(result: AccountingPeriodCloseResult): void {
    result.closedRuns.forEach(run => {
      const company = this.companies.find(item => item.id === run.companyId);
      if (company) {
        company.accountingClosedTo = run.closedToDate;
      }
    });
  }
}
